import { MessageSquare } from "lucide-react";
import type { Session } from "@/lib/types";
import type { SessionSource } from "@/lib/store";
import { EmptyState, ViewSection, relativeTime } from "@/views/shared";
import { HomeRow, HomeRowList, HomeRowsSkeleton } from "./HomeRow";

export interface RecentSessionEntry {
  session: Session;
  source: SessionSource;
  machineLabel: string;
}

// Dot styling mirrors SessionList's status treatment so "live" and "mirror"
// read identically everywhere.
function StatusDot({ status }: { status: Session["status"] }) {
  if (status === "live") {
    return (
      <span className="inline-flex items-center gap-1 text-primary">
        <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_6px_hsl(var(--primary))]" />
        live
      </span>
    );
  }
  if (status === "mirror") {
    return (
      <span className="inline-flex items-center gap-1 text-accent">
        <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
        mirror
      </span>
    );
  }
  return null;
}

export function RecentSessions({
  entries,
  loaded,
  onOpen,
}: {
  entries: RecentSessionEntry[];
  loaded: boolean;
  onOpen: (entry: RecentSessionEntry) => void;
}) {
  return (
    <ViewSection title="Jump back in">
      {!loaded ? (
        <HomeRowsSkeleton rows={3} />
      ) : entries.length === 0 ? (
        <EmptyState message="No sessions yet. Start one to see it here." />
      ) : (
        <HomeRowList>
          {entries.map((entry) => {
            const s = entry.session;
            return (
              <HomeRow key={`${entry.source}:${s.id}`} onClick={() => onOpen(entry)}>
                <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-foreground">
                    {s.title || s.id.slice(0, 8)}
                  </div>
                  <div className="flex items-center gap-1.5 truncate text-[10px] text-muted-foreground">
                    <span className="truncate font-mono">{s.projectSlug}</span>
                    <span aria-hidden>&middot;</span>
                    <span className="shrink-0">{entry.machineLabel}</span>
                    <span aria-hidden>&middot;</span>
                    <span className="shrink-0">
                      {relativeTime(new Date(s.lastMessageAt ?? s.updatedAt).toISOString())}
                    </span>
                    <StatusDot status={s.status} />
                  </div>
                </div>
              </HomeRow>
            );
          })}
        </HomeRowList>
      )}
    </ViewSection>
  );
}
